import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Phone, Clock, CheckCircle, XCircle, 
  Navigation, Wrench, AlertTriangle, ChevronRight,
  Play, Pause, Flag, RefreshCw, User, Coffee, Ban,
  Newspaper, FileText, Star, CreditCard, Calendar, X, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Müsaitlik durumu config
const AVAILABILITY_CONFIG = {
  available: { 
    label: 'Müsait', 
    emoji: '🟢',
    color: 'bg-green-500',
    textColor: 'text-green-500',
    bgLight: 'bg-green-500/10',
    icon: CheckCircle
  },
  busy: { 
    label: 'Meşgul', 
    emoji: '🔴',
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgLight: 'bg-red-500/10',
    icon: Ban
  },
  off_duty: { 
    label: 'İzinli', 
    emoji: '⚫',
    color: 'bg-gray-500',
    textColor: 'text-gray-500',
    bgLight: 'bg-gray-500/10',
    icon: Coffee
  }
};

// Durum bilgileri
const STATUS_CONFIG = {
  assigned: { 
    label: 'Atandı', 
    color: 'bg-blue-500', 
    nextStatus: 'accepted',
    nextLabel: 'Kabul Et',
    nextIcon: CheckCircle
  },
  accepted: { 
    label: 'Kabul Edildi', 
    color: 'bg-indigo-500',
    nextStatus: 'en_route',
    nextLabel: 'Yola Çık',
    nextIcon: Navigation
  },
  en_route: { 
    label: 'Yolda', 
    color: 'bg-orange-500',
    nextStatus: 'arrived',
    nextLabel: 'Vardım',
    nextIcon: MapPin
  },
  arrived: { 
    label: 'Varıldı', 
    color: 'bg-yellow-500',
    nextStatus: 'in_progress',
    nextLabel: 'Başladım',
    nextIcon: Play
  },
  in_progress: { 
    label: 'Çalışılıyor', 
    color: 'bg-purple-500',
    nextStatus: 'completed',
    nextLabel: 'Tamamladım',
    nextIcon: Flag
  },
  pending_approval: { 
    label: 'Onay Bekliyor', 
    color: 'bg-cyan-500',
    nextStatus: null,
    nextLabel: null
  },
  approved: { 
    label: 'Onaylandı', 
    color: 'bg-green-500',
    nextStatus: null,
    nextLabel: null
  },
  completed: { 
    label: 'Tamamlandı', 
    color: 'bg-green-600',
    nextStatus: null,
    nextLabel: null
  },
  rejected: { 
    label: 'Reddedildi', 
    color: 'bg-red-500',
    nextStatus: null,
    nextLabel: null
  },
  cancelled: { 
    label: 'İptal Edildi', 
    color: 'bg-gray-500',
    nextStatus: null,
    nextLabel: null
  },
  paid: { 
    label: 'Ödendi', 
    color: 'bg-emerald-500',
    nextStatus: null,
    nextLabel: null
  }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Kritik', color: 'text-red-500 bg-red-500/10' },
  high: { label: 'Yüksek', color: 'text-orange-500 bg-orange-500/10' },
  medium: { label: 'Normal', color: 'text-yellow-500 bg-yellow-500/10' },
  low: { label: 'Düşük', color: 'text-green-500 bg-green-500/10' }
};

const TechnicianPage = () => {
  const { user, isSimulationActive, simulatedRole, stopRoleSimulation } = useAuth();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState('active'); // active, completed, all
  const [profile, setProfile] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('work'); // work, contracts, references, membership
  const [contracts, setContracts] = useState([]);
  const [references, setReferences] = useState([]);
  const [membership, setMembership] = useState(null);
  
  // Çift tıklama koruması için
  const [lastClickTime, setLastClickTime] = useState(0);
  const CLICK_DEBOUNCE_MS = 2000; // 2 saniye bekleme süresi
  
  // Konum takip state'leri
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);

  // Konum güncelleme fonksiyonu
  const updateLocationToServer = async (position) => {
    try {
      const token = localStorage.getItem('hims_token');
      await fetch(`${API_URL}/api/technicians/my/location`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed
        })
      });
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude
      });
    } catch (error) {
      console.error('Konum güncellenemedi:', error);
    }
  };

  // Konum izni iste ve takibi başlat
  const startLocationTracking = () => {
    if (!navigator.geolocation) {
      setLocationError('Tarayıcınız konum servisini desteklemiyor');
      toast.error('Tarayıcınız konum servisini desteklemiyor');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationEnabled(true);
        setLocationError(null);
        updateLocationToServer(position);
        toast.success('Konum izni verildi, takip başladı');
        setIsTracking(true);
      },
      (error) => {
        setLocationEnabled(false);
        let errorMsg = 'Konum alınamadı';
        if (error.code === error.PERMISSION_DENIED) {
          errorMsg = 'Konum izni reddedildi. Lütfen tarayıcı ayarlarından izin verin.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMsg = 'Konum bilgisi alınamıyor';
        } else if (error.code === error.TIMEOUT) {
          errorMsg = 'Konum alma zaman aşımı';
        }
        setLocationError(errorMsg);
        toast.error(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Periyodik konum güncelleme (30 saniyede bir)
  useEffect(() => {
    let intervalId;
    
    if (isTracking && locationEnabled) {
      intervalId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          updateLocationToServer,
          (error) => console.error('Konum güncelleme hatası:', error),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      }, 30000); // 30 saniye
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isTracking, locationEnabled]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/technicians/my/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Profil yüklenemedi:', error);
    }
  };

  const updateAvailabilityStatus = async (newStatus) => {
    try {
      setStatusUpdating(true);
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/technicians/my/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setProfile(prev => ({ ...prev, availability_status: newStatus, is_available: data.is_available }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Durum güncellenemedi');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setStatusUpdating(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/work-orders/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data);
      } else {
        toast.error('İş emirleri yüklenemedi');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/contracts/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Sözleşmeler yüklenemedi:', error);
    }
  };

  const fetchReferences = async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/technicians/my/references`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferences(data);
      }
    } catch (error) {
      console.error('Referanslar yüklenemedi:', error);
    }
  };

  const fetchMembership = async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/technicians/my/membership`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembership(data);
      }
    } catch (error) {
      console.error('Üyelik bilgisi yüklenemedi:', error);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
    fetchProfile();
    fetchContracts();
    fetchReferences();
    fetchMembership();
  }, []);

  const updateStatus = async (workOrderId, newStatus) => {
    // Çift tıklama koruması
    const now = Date.now();
    if (now - lastClickTime < CLICK_DEBOUNCE_MS) {
      toast.warning('Lütfen bekleyin, işlem devam ediyor...');
      return;
    }
    setLastClickTime(now);
    
    // Zaten güncelleme yapılıyorsa engelle
    if (updating) {
      toast.warning('Bir işlem zaten devam ediyor');
      return;
    }
    
    try {
      // "accepted" veya "en_route" durumlarında konum izni zorunlu
      if (['accepted', 'en_route'].includes(newStatus) && !locationEnabled) {
        // Konum izni iste
        const permissionGranted = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setLocationEnabled(true);
              setCurrentLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude
              });
              updateLocationToServer(position);
              resolve(true);
            },
            (error) => {
              let errorMsg = 'Konum izni gerekli!';
              if (error.code === error.PERMISSION_DENIED) {
                errorMsg = 'İşi kabul etmek için konum izni vermelisiniz. Tarayıcı ayarlarından izin verin.';
              }
              toast.error(errorMsg);
              resolve(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        });

        if (!permissionGranted) {
          return; // Konum izni verilmediyse işlemi iptal et
        }
      }

      // "en_route" durumunda canlı takibi başlat
      if (newStatus === 'en_route') {
        setIsTracking(true);
        toast.info('Canlı konum takibi başladı (30sn aralıklarla güncellenir)');
      }

      // "arrived" veya sonraki durumlarda takibi durdur
      if (['arrived', 'in_progress', 'completed'].includes(newStatus)) {
        setIsTracking(false);
      }

      setUpdating(workOrderId);
      const token = localStorage.getItem('hims_token');
      
      // Get current location for GPS tracking
      let latitude = null;
      let longitude = null;
      
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          latitude = position.coords.latitude;
          longitude = position.coords.longitude;
          // Sunucuya da gönder
          updateLocationToServer(position);
        } catch (e) {
          console.log('GPS alınamadı:', e);
        }
      }
      
      const response = await fetch(`${API_URL}/api/work-orders/${workOrderId}/status?status=${newStatus}${latitude ? `&latitude=${latitude}&longitude=${longitude}` : ''}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Durum güncellendi');
        fetchWorkOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Güncelleme başarısız');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setUpdating(null);
    }
  };

  // Reddetme modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingWorkOrderId, setRejectingWorkOrderId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Onay modal state (kritik adımlar için)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { workOrderId, newStatus, label }

  const REJECT_REASONS = [
    { value: 'not_available', label: 'Müsait değilim' },
    { value: 'not_my_specialty', label: 'Uzmanlık alanım değil' },
    { value: 'too_far', label: 'Uzak mesafe' },
    { value: 'other', label: 'Diğer' }
  ];

  // Kritik adımlar - onay gerektirenler
  const CRITICAL_STATUSES = ['completed', 'in_progress'];

  const handleStatusClick = (workOrderId, newStatus, label) => {
    if (CRITICAL_STATUSES.includes(newStatus)) {
      // Kritik adımlarda onay iste
      setPendingAction({ workOrderId, newStatus, label });
      setConfirmModalOpen(true);
    } else {
      // Normal adımlarda direkt ilerle
      updateStatus(workOrderId, newStatus);
    }
  };

  const confirmStatusUpdate = () => {
    if (pendingAction) {
      updateStatus(pendingAction.workOrderId, pendingAction.newStatus);
      setConfirmModalOpen(false);
      setPendingAction(null);
    }
  };

  const openRejectModal = (workOrderId) => {
    setRejectingWorkOrderId(workOrderId);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const rejectWorkOrder = async () => {
    if (!rejectReason) {
      toast.error('Lütfen bir sebep seçin');
      return;
    }
    
    try {
      setUpdating(rejectingWorkOrderId);
      const token = localStorage.getItem('hims_token');
      
      const response = await fetch(`${API_URL}/api/work-orders/${rejectingWorkOrderId}/status?status=rejected&notes=${encodeURIComponent(rejectReason)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('İş emri reddedildi');
        setRejectModalOpen(false);
        fetchWorkOrders();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    } finally {
      setUpdating(null);
    }
  };

  const openInMaps = (address) => {
    if (!address) {
      toast.error('Adres bulunamadı');
      return;
    }
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  // Filter work orders
  const filteredOrders = workOrders.filter(wo => {
    if (filter === 'active') {
      return !['completed', 'pending_approval', 'approved', 'paid', 'rejected', 'cancelled'].includes(wo.status);
    }
    if (filter === 'completed') {
      return ['completed', 'pending_approval', 'approved', 'paid'].includes(wo.status);
    }
    return true;
  });

  const activeCount = workOrders.filter(wo => 
    !['completed', 'pending_approval', 'approved', 'paid', 'rejected', 'cancelled'].includes(wo.status)
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Simülasyon Banner'ı */}
      {isSimulationActive && (
        <div className="sticky top-0 z-50 bg-red-500 text-white px-4 py-2 flex items-center justify-between" data-testid="simulation-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="font-medium text-sm">SİMÜLASYON MODU:</span>
            <span className="text-sm">Usta/Teknisyen</span>
          </div>
          <button 
            onClick={() => {
              stopRoleSimulation();
              navigate('/dashboard');
            }}
            className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm"
            data-testid="stop-simulation-btn"
          >
            <X className="w-4 h-4" />
            Simülasyonu Bitir
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-4" style={{ top: isSimulationActive ? '40px' : '0' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">İş Emirlerim</h1>
            <p className="text-sm text-muted-foreground">{activeCount} aktif iş emri</p>
          </div>
          <button
            onClick={() => { fetchWorkOrders(); fetchProfile(); }}
            className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Profil & Durum Kartı */}
        {profile && (
          <div className="mt-4 p-4 rounded-xl bg-muted/50 border border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                {(profile.full_name || 'XX').split(' ').map(n => n[0] || '').join('').slice(0, 2)}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground">{profile.full_name}</h3>
                <p className="text-xs text-muted-foreground">
                  {profile.completed_jobs || 0} iş tamamlandı
                </p>
              </div>
            </div>
            
            {/* Durum Seçici */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Durumunuz:</p>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(AVAILABILITY_CONFIG).map(([status, config]) => {
                  const isActive = profile.availability_status === status;
                  const Icon = config.icon;
                  return (
                    <button
                      key={status}
                      onClick={() => updateAvailabilityStatus(status)}
                      disabled={statusUpdating || isActive}
                      className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-all ${
                        isActive 
                          ? `${config.color} text-white shadow-lg scale-105` 
                          : `${config.bgLight} ${config.textColor} hover:scale-102`
                      } ${statusUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`status-${status}`}
                    >
                      <span className="text-lg">{config.emoji}</span>
                      <span className="text-xs font-medium">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Konum Takip Durumu */}
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className={`w-4 h-4 ${locationEnabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                  <span className="text-xs text-muted-foreground">
                    {isTracking ? 'Canlı takip aktif' : locationEnabled ? 'Konum izni verildi' : 'Konum kapalı'}
                  </span>
                </div>
                {isTracking && (
                  <span className="flex items-center gap-1 text-xs text-green-500">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Aktif
                  </span>
                )}
              </div>
              {locationError && (
                <p className="text-xs text-red-500 mt-1">{locationError}</p>
              )}
            </div>
          </div>
        )}

        {/* Haberler Linki */}
        <button 
          onClick={() => navigate('/technician/news')}
          className="mt-3 w-full flex items-center gap-3 p-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 transition-colors text-left"
          data-testid="news-link"
        >
          <Newspaper className="w-5 h-5 text-blue-500" />
          <span className="text-sm font-medium text-foreground">Haber Merkezi</span>
          <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto" />
        </button>
        
        {/* Ana Sekmeler */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {[
            { id: 'work', label: 'İş Emirleri', icon: Wrench, count: workOrders.length },
            { id: 'contracts', label: 'Sözleşmeler', icon: FileText, count: contracts.length },
            { id: 'references', label: 'Referanslar', icon: Star, count: references.length },
            { id: 'membership', label: 'Üyelik', icon: CreditCard, count: null }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* İş Emirleri Tab */}
      {activeTab === 'work' && (
        <>
        {/* Filter Tabs */}
        <div className="flex gap-2 mt-4 px-4">
          {[
            { key: 'active', label: 'Aktif' },
            { key: 'completed', label: 'Tamamlanan' },
            { key: 'all', label: 'Tümü' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.key 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

      {/* Work Orders List */}
      <div className="p-4 space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <Wrench className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {filter === 'active' ? 'Aktif iş emri bulunmuyor' : 'İş emri bulunmuyor'}
            </p>
          </div>
        ) : (
          filteredOrders.map(wo => {
            const statusConfig = STATUS_CONFIG[wo.status] || STATUS_CONFIG.assigned;
            const priorityConfig = PRIORITY_CONFIG[wo.priority || wo.fault_priority] || PRIORITY_CONFIG.medium;
            const NextIcon = statusConfig.nextIcon;
            
            return (
              <div 
                key={wo.id} 
                className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm"
                data-testid={`work-order-${wo.id}`}
              >
                {/* Status Bar with Work Order Number */}
                <div className={`${statusConfig.color} px-4 py-2 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">
                      İŞ EMRİ #{wo.work_order_number || wo.id?.slice(-6).toUpperCase() || 'N/A'}
                    </span>
                    {/* İş Tipi Rozeti */}
                    {wo.job_type && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        wo.job_type === 'installation' ? 'bg-purple-500/80 text-white' :
                        wo.job_type === 'maintenance' ? 'bg-cyan-500/80 text-white' :
                        'bg-orange-500/80 text-white'
                      }`}>
                        {wo.job_type === 'installation' ? '🔩 Montaj' :
                         wo.job_type === 'maintenance' ? '🔍 Bakım' :
                         '🔧 Arıza'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{statusConfig.label}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityConfig.color}`}>
                      {priorityConfig.label}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4 space-y-4">
                  {/* Site & Location */}
                  <div>
                    <h3 className="font-semibold text-lg text-foreground">{wo.site_name || 'Site'}</h3>
                    {wo.location_name && (
                      <p className="text-sm text-muted-foreground">
                        {wo.location_name}
                        {wo.floor_section && ` - ${wo.floor_section}`}
                      </p>
                    )}
                  </div>
                  
                  {/* Site Manager Contact */}
                  {wo.site_manager_phone && (
                    <a
                      href={`tel:${wo.site_manager_phone}`}
                      className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors"
                    >
                      <Phone className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-foreground">{wo.site_manager_name || 'Site Yöneticisi'}</span>
                      <span className="text-sm text-green-600 font-medium">{wo.site_manager_phone}</span>
                    </a>
                  )}
                  
                  {/* Device Info for Installation/Maintenance */}
                  {(wo.job_type === 'installation' || wo.job_type === 'maintenance') && wo.device_info && (
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <p className="text-xs text-purple-600 font-medium mb-1">Cihaz Bilgisi</p>
                      <p className="text-sm text-foreground">
                        {wo.device_info.brand && `${wo.device_info.brand} `}
                        {wo.device_info.model && `${wo.device_info.model}`}
                        {wo.device_info.serial_no && ` - SN: ${wo.device_info.serial_no}`}
                      </p>
                    </div>
                  )}
                  
                  {/* Station */}
                  {wo.station_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <Wrench className="w-4 h-4 text-primary" />
                      <span className="text-foreground">{wo.station_name}</span>
                      {wo.station_ocpp_id && (
                        <span className="text-muted-foreground">({wo.station_ocpp_id})</span>
                      )}
                    </div>
                  )}
                  
                  {/* Fault Description */}
                  {(wo.fault_title || wo.title) && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm text-foreground">{wo.fault_title || wo.title}</p>
                          {(wo.fault_description || wo.description) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {wo.fault_description || wo.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Address */}
                  {wo.full_address && (
                    <button
                      onClick={() => openInMaps(wo.full_address)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition-colors text-left"
                      data-testid={`map-btn-${wo.id}`}
                    >
                      <MapPin className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground truncate">{wo.full_address}</p>
                        <p className="text-xs text-blue-500 font-medium">Haritada Aç</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-blue-500" />
                    </button>
                  )}
                  
                  {/* Created Date */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    <span>
                      {wo.created_at ? new Date(wo.created_at).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                </div>
                
                {/* Action Buttons */}
                {statusConfig.nextStatus && (
                  <div className="border-t border-border p-4 space-y-2">
                    {/* Main Action Button */}
                    <button
                      onClick={() => handleStatusClick(wo.id, statusConfig.nextStatus, statusConfig.nextLabel)}
                      disabled={updating === wo.id || updating !== null}
                      className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
                        (updating === wo.id || updating !== null)
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]'
                      }`}
                      data-testid={`action-btn-${wo.id}`}
                    >
                      {updating === wo.id ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                          Güncelleniyor...
                        </>
                      ) : (
                        <>
                          {NextIcon && <NextIcon className="w-6 h-6" />}
                          {statusConfig.nextLabel}
                        </>
                      )}
                    </button>
                    
                    {/* Reject Button (only for assigned status) */}
                    {wo.status === 'assigned' && (
                      <button
                        onClick={() => openRejectModal(wo.id)}
                        disabled={updating === wo.id}
                        className="w-full py-3 rounded-xl font-medium text-red-500 bg-red-500/10 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
                        data-testid={`reject-btn-${wo.id}`}
                      >
                        <XCircle className="w-5 h-5" />
                        Reddet
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      </>
      )}

      {/* Sözleşmeler Tab */}
      {activeTab === 'contracts' && (
        <div className="p-4 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Sözleşmelerim</h2>
          {contracts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Henüz sözleşmeniz bulunmuyor</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map(contract => (
                <div key={contract.id} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-foreground">{contract.site_name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs ${contract.is_active ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                      {contract.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Başlangıç: {contract.start_date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Bitiş: {contract.end_date}</span>
                    </div>
                    <div>İş Başı: {contract.per_job_rate} ₺</div>
                  </div>
                  {contract.notes && (
                    <p className="mt-2 text-sm text-muted-foreground">{contract.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Referanslar Tab */}
      {activeTab === 'references' && (
        <div className="p-4 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Referanslarım ({references.length})</h2>
          {references.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Henüz referansınız bulunmuyor</p>
              <p className="text-xs mt-1">İş emirleri tamamlanınca otomatik eklenir</p>
            </div>
          ) : (
            <div className="space-y-3">
              {references.map(ref => (
                <div key={ref.id} className="bg-card rounded-xl p-4 border border-border">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-foreground">{ref.site_name}</h3>
                    <span className="text-xs text-muted-foreground">
                      {new Date(ref.completed_at).toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{ref.fault_title}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded text-xs">
                      {ref.fault_category || 'Genel'}
                    </span>
                  </div>
                  {ref.description && (
                    <p className="mt-2 text-sm text-muted-foreground">{ref.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Üyelik Tab */}
      {activeTab === 'membership' && (
        <div className="p-4 space-y-4">
          <h2 className="text-lg font-semibold text-foreground">Üyelik Durumum</h2>
          {membership ? (
            <div className="bg-card rounded-xl p-6 border border-border">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  membership.is_expired ? 'bg-red-500/20' : 'bg-green-500/20'
                }`}>
                  <CreditCard className={`w-8 h-8 ${membership.is_expired ? 'text-red-500' : 'text-green-500'}`} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    {membership.membership_type === 'premium' ? 'Premium Üyelik' : 'Standart Üyelik'}
                  </h3>
                  <p className={`text-sm ${membership.is_expired ? 'text-red-500' : 'text-green-500'}`}>
                    {membership.is_expired ? 'Süresi Doldu' : 'Aktif'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Başlangıç</span>
                  <span className="font-medium">{membership.membership_start?.slice(0,10) || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Bitiş</span>
                  <span className="font-medium">{membership.membership_end?.slice(0,10) || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Kalan Gün</span>
                  <span className={`font-medium ${membership.days_remaining <= 20 ? 'text-orange-500' : ''}`}>
                    {membership.days_remaining !== null ? `${membership.days_remaining} gün` : '-'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Ücret Durumu</span>
                  <span className="font-medium">
                    {membership.membership_fee_active ? 'Ücretli' : 'Ücretsiz'}
                  </span>
                </div>
              </div>

              {membership.is_expired && (
                <Link 
                  to="/membership-payment" 
                  className="mt-6 w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-5 h-5" />
                  Üyeliği Yenile
                </Link>
              )}

              {!membership.membership_fee_active && (
                <p className="mt-4 text-center text-sm text-muted-foreground">
                  Şu an üyelik ücretsizdir.
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Üyelik bilgisi yüklenemedi</p>
            </div>
          )}
        </div>
      )}

      {/* Reddetme Modal */}
      {rejectModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-4">İş Emrini Reddet</h3>
              <p className="text-muted-foreground mb-4">Lütfen reddetme sebebini seçin:</p>
              
              <div className="space-y-2 mb-6">
                {REJECT_REASONS.map(reason => (
                  <label 
                    key={reason.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      rejectReason === reason.label 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="rejectReason"
                      value={reason.label}
                      checked={rejectReason === reason.label}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-foreground">{reason.label}</span>
                  </label>
                ))}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectModalOpen(false)}
                  className="flex-1 py-3 rounded-xl font-medium border border-border hover:bg-muted transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={rejectWorkOrder}
                  disabled={!rejectReason || updating}
                  className="flex-1 py-3 rounded-xl font-medium bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {updating ? 'İşleniyor...' : 'Reddet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onay Modal - Kritik Adımlar İçin */}
      {confirmModalOpen && pendingAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-foreground text-center mb-2">Emin misiniz?</h3>
              <p className="text-muted-foreground text-center mb-6">
                İş emri durumunu <strong>"{pendingAction.label}"</strong> olarak güncellemek üzeresiniz.
                {pendingAction.newStatus === 'completed' && (
                  <span className="block mt-2 text-orange-500 font-medium">
                    ⚠️ Bu işlem geri alınamaz!
                  </span>
                )}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setPendingAction(null);
                  }}
                  className="flex-1 py-3 rounded-xl font-medium border border-border hover:bg-muted transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  onClick={confirmStatusUpdate}
                  disabled={updating}
                  className="flex-1 py-3 rounded-xl font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {updating ? 'İşleniyor...' : 'Onayla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianPage;
